import { useCallback, useMemo, useState } from "react"
import { Button, Form, Modal, ModalProps } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { formTranslation } from "../../locales/form"
import DatePicker, { ReactDatePickerProps, registerLocale } from "react-datepicker"
import MenuItemRow from "../MenuItemRow/MenuItemRow"
import ru from "date-fns/locale/ru"
import enGb from "date-fns/locale/en-GB"
import styles from "./CalendarModal.module.scss"
import "./CalendarModalDatepicker.scss"
import format from "date-fns/format"
import { beautifyDateToHMSString } from "../../utility/common/time"
import { CALENDAR_MODAL_CONST } from "./CalendarModal.const"

registerLocale("ru", ru)
registerLocale("en", enGb)

const { tNamespace, tPaths, dateFormat, getDatesBetween, handleChangeTime } = CALENDAR_MODAL_CONST

export type PickerProps = Pick<ReactDatePickerProps, "minDate" | "maxDate" | "selectsRange" | "showTimeInput">

export interface CalendarModalProps extends ModalProps, PickerProps {
    show: boolean
    onClose: () => void
    onSubmit: (dates: Date[]) => void
    datesBetween?: boolean
}

const CalendarModal = (props: CalendarModalProps) => {
    const { t, i18n } = useTranslation(tNamespace)
    const {
        show,
        onClose,
        onSubmit,
        datesBetween,
        minDate,
        maxDate,
        selectsRange = false,
        showTimeInput = false,
        adjustDateOnChange = false,
        ...modalProps
    } = props

    const now = useMemo(() => new Date(), [])
    const [startDate, setStartDate] = useState<Date>(minDate ?? now)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [error, setError] = useState("")
    const isRangeMustBeSelected = selectsRange && !endDate

    const handleSubmit = useCallback(() => {
        if (!endDate) {
            onSubmit([startDate])
        } else {
            onSubmit(datesBetween ? getDatesBetween(startDate, endDate) : [startDate, endDate])
        }
        onClose()
    }, [datesBetween, startDate, endDate, onSubmit, onClose])

    const handleChange = useCallback(
        (dates: [Date, Date] | Date | null) => {
            if (dates) {
                if (Array.isArray(dates)) {
                    const [start, end] = dates
                    setStartDate(start)
                    setEndDate(end)
                    validateDates(selectsRange, start, end)
                } else {
                    setStartDate(dates)
                    setEndDate(null)
                    validateDates(selectsRange, dates, null)
                }
            }
        },
        [selectsRange]
    )

    const handleChangeStartTime = handleChangeTime(startDate, setStartDate, (date: Date) =>
        validateDates(selectsRange, date, endDate)
    )
    const handleChangeEndTime = handleChangeTime(endDate, setEndDate, (date: Date) =>
        validateDates(selectsRange, startDate, date)
    )

    const validateDates = (selectsRange: boolean, startDate: Date, endDate: Date | null) => {
        if (selectsRange) {
            if (!endDate) {
                setError(t(tPaths.errorRangePick))
            } else if (startDate > endDate) {
                setError(t(tPaths.errorRangeInvalid))
            } else {
                setError("")
            }
        }
    }

    return (
        <Modal {...modalProps} dialogClassName={styles.calendar} show={show} onHide={onClose}>
            <div className={styles.calendar__content}>
                <div className={styles.calendar__sidebar}>
                    <MenuItemRow
                        title={t(tPaths.today)}
                        onClick={() => {
                            setStartDate(now)
                            setEndDate(null)
                        }}
                    />
                </div>
                <div className={styles.calendar__datePicker}>
                    <DatePicker
                        adjustDateOnChange={adjustDateOnChange}
                        selected={startDate}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={handleChange}
                        monthsShown={2}
                        locale={i18n.language}
                        selectsRange={selectsRange}
                        minDate={minDate}
                        maxDate={maxDate}
                        inline
                        calendarClassName="calendar-datepicker"
                        disabledKeyboardNavigation
                    />
                    <div className={styles.calendar__inputs}>
                        <div>
                            <div className={styles.calendar__cell}>{t(tPaths.date)}</div>
                            {showTimeInput && <div className={styles.calendar__cell}>{t(tPaths.time)}</div>}
                        </div>
                        <div>
                            <div className={styles.calendar__cell}>
                                <Form.Control
                                    value={format(startDate, dateFormat)}
                                    className={styles.calendar__input}
                                    readOnly
                                />
                            </div>
                            {showTimeInput && (
                                <div className={styles.calendar__cell}>
                                    <Form.Control
                                        type="time"
                                        step={1}
                                        value={beautifyDateToHMSString(startDate)}
                                        className={styles.calendar__input}
                                        aria-label={t(tPaths.time)}
                                        onChange={handleChangeStartTime}
                                    />
                                </div>
                            )}
                        </div>
                        {selectsRange && (
                            <>
                                <div>
                                    <div className={styles.calendar__cell}>-</div>
                                    {showTimeInput && <div className={styles.calendar__cell}>-</div>}
                                </div>
                                <div>
                                    <div className={styles.calendar__cell}>
                                        <Form.Control
                                            value={endDate ? format(endDate, dateFormat) : ""}
                                            className={styles.calendar__input}
                                            readOnly
                                        />
                                    </div>
                                    {showTimeInput && (
                                        <div className={styles.calendar__cell}>
                                            <Form.Control
                                                type="time"
                                                step={1}
                                                value={endDate ? beautifyDateToHMSString(endDate) : ""}
                                                onChange={handleChangeEndTime}
                                                className={styles.calendar__input}
                                                aria-label={t(tPaths.time)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.calendar__error}>{error}</div>
                </div>
            </div>

            <Modal.Footer>
                <Button variant="light" onClick={onClose}>
                    {t(formTranslation.cancel)}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={Boolean(error || isRangeMustBeSelected)}>
                    {t(formTranslation.apply)}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default CalendarModal
